import React, { useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from "@material-ui/core";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import ImageIcon from "@material-ui/icons/Image";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import Webcam from "react-webcam";
import useCamera from "../../../../hooks/useCamera";
import { useStore } from "../../../../hooks/useStore";
import NotCamera from "../../../Scan/NotCamera/NotCamera";
import DocumentImageViewer from "./DocumentImageViewer";
import UserContext from "../../../../context/user-context";
import { dataURLtoFile } from "../../../../utils/imageUtil";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  preview: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    backgroundColor: "#F8FAFC",
    color: "#98A2B3",
    cursor: "pointer",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  previewCompact: {
    backgroundColor: "white",
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
  },
  previewEmpty: {
    border: "2px dashed #D1D5DC",
    backgroundColor: "#F3F4F6",
  },
  previewWithImage: {
    height: "350px",
    padding: "20px",
    alignItems: "stretch",
    justifyContent: "stretch",
  },
  imageFrame: {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "#E5E7EB",
  },
  clearButton: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.96)",
    color: "#D92D20",
    border: "1px solid rgba(217, 45, 32, 0.35)",
    boxShadow: "0 2px 8px rgba(217, 45, 32, 0.25)",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      color: "#B42318",
      boxShadow: "0 3px 10px rgba(180, 35, 24, 0.32)",
    },
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "6px",
    fontSize: "24px",
  },
  emptyStateCircular: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "12px",
    fontSize: "24px",
  },
  iconCircleWrapper: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.06)",
  },
  iconCircle: {
    fontSize: "40px",
    width: "40px",
    height: "40px",
    color: "#6A7282",
  },
  labelCircular: {
    color: "#475569",
    fontSize: "25px",
    lineHeight: "26px",
    fontWeight: 600,
    textAlign: "center",
  },
  label: {
    color: "#6A7282",
    fontSize: "22px",
    lineHeight: "24px",
    fontWeight: 400,
  },
  labelCompact: {
    color: "#6A7282",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  cameraDialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    width: "100%",
  },
  videoWrapper: {
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#000",
    minHeight: "420px",
    [theme.breakpoints.down("sm")]: {
      minHeight: "56vh",
      borderRadius: "8px",
    },
  },
  video: {
    width: "100%",
    height: "420px",
    display: "block",
    objectFit: "cover",
    [theme.breakpoints.down("sm")]: {
      height: "56vh",
    },
  },
  dialogContent: {
    [theme.breakpoints.down("sm")]: {
      padding: "12px",
    },
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  dragActive: {
    borderColor: "#3b82f6 !important",
    backgroundColor: "rgba(59, 130, 246, 0.08) !important",
    borderStyle: "solid !important",
  },
}));

export type DocumentPicturePreviewHandle = {
  openCamera: () => void;
};

type DocumentPicturePreviewProps = {
  label: string;
  imageUrl?: string;
  onUpload?: (file: File | null, dataUrl?: string) => void;
  previewHeight?: number | string;
  placeholderHeight?: number | string;
  labelSize?: "normal" | "compact";
  backgroundColor?: string;
  borderStyle?: "normal" | "dashed";
  iconVariant?: "default" | "circularImage";
  allowClear?: boolean;
  allowImageViewer?: boolean;
  imageFileName?: string;
  readOnly?: boolean;
};

const DocumentPicturePreview = React.forwardRef<
  DocumentPicturePreviewHandle,
  DocumentPicturePreviewProps
>(function DocumentPicturePreview(
  {
    label,
    imageUrl = "",
    onUpload,
    previewHeight,
    placeholderHeight,
    labelSize = "normal",
    backgroundColor,
    borderStyle = "normal",
    iconVariant = "default",
    allowClear = false,
    allowImageViewer = false,
    imageFileName,
    readOnly = false,
  },
  ref
) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [previewUrl, setPreviewUrl] = useState<string>(imageUrl);
  const resolvedPreviewHeight =
    previewHeight !== undefined
      ? typeof previewHeight === "number"
        ? `${previewHeight}px`
        : previewHeight
      : undefined;
  const resolvedPlaceholderHeight =
    placeholderHeight !== undefined
      ? typeof placeholderHeight === "number"
        ? `${placeholderHeight}px`
        : placeholderHeight
      : "96px";
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { state } = useContext(UserContext);

  useImperativeHandle(ref, () => ({ openCamera: () => setIsCameraOpen(true) }));
  const { getListOfCameras } = useCamera();
  const { cameraStore } = useStore();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (readOnly) {
      if (hasImage && allowImageViewer) {
        setIsViewerOpen(true);
      }
      return;
    }
    if (hasImage && allowImageViewer) {
      setIsViewerOpen(true);
      return;
    }
    setMenuAnchorEl(event.currentTarget);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!readOnly) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const validateFile = (file: File): boolean => {
    const isAcceptedType =
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      file.name.toLowerCase().endsWith(".png") ||
      file.name.toLowerCase().endsWith(".jpg") ||
      file.name.toLowerCase().endsWith(".jpeg");

    const isAcceptedSize = file.size <= 10 * 1024 * 1024; // 10MB

    if (!isAcceptedType) {
      alert("Solo se permiten imágenes en formato PNG o JPEG/JPG.");
      return false;
    }

    if (!isAcceptedSize) {
      alert("El tamaño máximo permitido para la imagen es de 10MB.");
      return false;
    }

    return true;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (readOnly) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!validateFile(file)) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setPreviewUrl(dataUrl);
        onUpload?.(file, dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateFile(file)) {
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setPreviewUrl(dataUrl);
        onUpload?.(file, dataUrl);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const stopWebcamTracks = () => {
    if (webcamRef.current) {
      if (webcamRef.current.stream) {
        webcamRef.current.stream.getTracks().forEach((track) => track.stop());
      }
      if (webcamRef.current.video?.srcObject) {
        try {
          const stream = webcamRef.current.video.srcObject as MediaStream;
          stream.getTracks().forEach((track) => track.stop());
        } catch (e) {
          console.error("Error stopping tracks on webcamRef:", e);
        }
        webcamRef.current.video.srcObject = null;
      }
    }
  };

  useEffect(() => {
    return () => {
      stopWebcamTracks();
    };
  }, []);

  const closeCameraDialog = () => {
    stopWebcamTracks();
    setIsCameraOpen(false);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
  };

  const hasImage = Boolean(previewUrl);
  let previewStyle: React.CSSProperties = {
    minHeight: resolvedPlaceholderHeight,
  };
  if (hasImage && previewHeight) {
    previewStyle = { height: resolvedPreviewHeight, minHeight: resolvedPreviewHeight };
  }
  const resolvedBorderStyle = borderStyle === "dashed" ? "2px dashed #A3B4C0" : undefined;

  const capture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      return;
    }

    setPreviewUrl(imageSrc);
    onUpload?.(dataURLtoFile(imageSrc, `document-photo-${Date.now()}.jpg`), imageSrc);
    closeCameraDialog();
  };

  const clearImage = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsViewerOpen(false);
    setPreviewUrl("");
    onUpload?.(null);
  };

  const clearImageFromViewer = () => {
    setPreviewUrl("");
    onUpload?.(null);
  };

  const handleRetake = () => {
    setIsViewerOpen(false);
    setIsCameraOpen(true);
  };

  useEffect(() => {
    setPreviewUrl(imageUrl);
  }, [imageUrl]);

  useEffect(() => {
    if (isCameraOpen) {
      getListOfCameras();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraOpen]);

  return (
    <div className={classes.root}>
      <div
        style={{
          ...previewStyle,
          ...(backgroundColor ? { backgroundColor } : {}),
          ...(resolvedBorderStyle ? { border: resolvedBorderStyle } : {}),
        }}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleClick(event as unknown as React.MouseEvent<HTMLElement>);
          }
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`${classes.preview} ${!hasImage ? classes.previewEmpty : ""} ${
          hasImage && !previewHeight ? classes.previewWithImage : ""
        } ${labelSize === "compact" ? classes.previewCompact : ""} ${
          isDragActive ? classes.dragActive : ""
        }`}
        role="button"
        tabIndex={0}
      >
        {hasImage ? (
          <div className={classes.imageFrame}>
            {allowClear && (
              <IconButton
                className={classes.clearButton}
                size="small"
                aria-label="eliminar imagen"
                onClick={clearImage}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
            <img src={previewUrl} alt={label} className={classes.image} />
          </div>
        ) : iconVariant === "circularImage" ? (
          <div className={classes.emptyStateCircular}>
            <div className={classes.iconCircleWrapper}>
              <ImageIcon className={classes.iconCircle} />
            </div>
            <span className={classes.labelCircular}>{label}</span>
          </div>
        ) : (
          <div className={classes.emptyState}>
            <CameraAltIcon fontSize="inherit" />
            <span className={labelSize === "compact" ? classes.labelCompact : classes.label}>
              {label}
            </span>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            setIsCameraOpen(true);
          }}
        >
          <CameraAltIcon style={{ marginRight: 8 }} />
          Tomar foto
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            fileInputRef.current?.click();
          }}
        >
          <ImageIcon style={{ marginRight: 8 }} />
          Buscar archivo
        </MenuItem>
      </Menu>

      <Dialog
        open={isCameraOpen}
        onClose={closeCameraDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {isCameraOpen && (
          <>
            <DialogTitle>
              {label}
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={closeCameraDialog}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
              <div className={classes.cameraDialogContent}>
                {cameraStore.empty ? (
                  <NotCamera />
                ) : (
                  <div className={classes.videoWrapper}>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        deviceId: cameraStore.deviceId || undefined,
                      }}
                      className={classes.video}
                    />
                  </div>
                )}
              </div>
            </DialogContent>

            <DialogActions>
              <Button onClick={closeCameraDialog}>Cancelar</Button>
              <Button
                onClick={capture}
                color="primary"
                variant="contained"
                disabled={cameraStore.empty}
              >
                Capturar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <DocumentImageViewer
        open={isViewerOpen}
        onClose={closeViewer}
        imageUrl={previewUrl}
        alt={label}
        fileName={imageFileName || label}
        allowDelete={allowClear}
        onDelete={clearImageFromViewer}
        onRetake={readOnly ? undefined : handleRetake}
      />
    </div>
  );
});

export default DocumentPicturePreview;
